import { Modal, Table, Button, message } from 'antd';
import { useEffect, useState } from 'react';
import type { Board } from './hooks/useBoards';
import { useBoardRoles } from './hooks/useBoards';
import { useUsers } from './hooks/useUsers';
import { getBoardRoleColumns, type UserRoleData } from './config/columns/boardRoleColumns';

interface BoardRoleModalProps {
  open: boolean;
  board: Board | null;
  onClose: () => void;
  onSubmit: (updates: Array<{ boardId: number; userId: number; boardRoles: string[] }>) => void;
  isSubmitting?: boolean;
}

type RoleType = 'TOTAL';
// type RoleType = 'READ' | 'WRITE' | 'DELETE';

const ConferenceRoleModal = ({ open, board, onClose, onSubmit, isSubmitting = false }: BoardRoleModalProps) => {
  const { users, loading: usersLoading } = useUsers();
  const { roles, loading: rolesLoading } = useBoardRoles(board?.board_id);
  const [userRoles, setUserRoles] = useState<UserRoleData[]>([]);
  const [changedUsers, setChangedUsers] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open && users.length > 0) {
      const activeUsers = users.filter(user => !user.isWithdrawnYN);
      // 게시판 권한 설정 모달에 띄운 [유저 리스트]와 [게시판 별 권한 설정된 유저 정보] 매핑 작업
      const mappedData: UserRoleData[] = activeUsers.map(user => {
          // roles에서 해당 사용자의 권한 정보 찾기
          const userRole = roles.find((r: any) => r.userId === user.userId);

          return {
              userId: user.userId,
              username: user.username,
              name: user.name,
              role: user.role,
              currentRoles: userRole?.permissions || [],
          };
      });

      // ADVISOR 권한을 가진 사용자를 최상단에 정렬
      const sortedData = mappedData.sort((a, b) => {
          if (a.role === 'ADVISOR' && b.role !== 'ADVISOR') return -1;
          if (a.role !== 'ADVISOR' && b.role === 'ADVISOR') return 1;
          return 0;
      });

      setUserRoles(sortedData);
      setChangedUsers(new Set());
    }
  }, [open, board?.board_id, users, roles]);

  const handleRoleChange = (userId: number, role: RoleType, checked: boolean) => {
    setUserRoles(prev => {
      const updated = prev.map(user => {
        if (user.userId === userId) {
            const currentRoles = user.updatedRoles || user.currentRoles;
            const newRoles = checked
                ? [...currentRoles, role]
                : currentRoles.filter(r => r !== role);

            return {
                ...user,
                updatedRoles: newRoles,
            };
        }
        return user;
      });

      const targetUser = updated.find(u => u.userId === userId);
      if (targetUser) {
        const currentRolesSorted = [...targetUser.currentRoles].sort();
        const updatedRolesSorted = [...(targetUser.updatedRoles || targetUser.currentRoles)].sort();
        const hasRealChange = JSON.stringify(currentRolesSorted) !== JSON.stringify(updatedRolesSorted);

        setChangedUsers(prevChanged => {
            const newChanged = new Set(prevChanged);
            if (hasRealChange) {
                newChanged.add(userId);
            } else {
                newChanged.delete(userId);
            }
            return newChanged;
        });
      }
      return updated;
    });
  };

  const handleSubmit = () => {
    if (!board) return;

    const updates: Array<{ boardId: number; userId: number; boardRoles: string[] }> = [];

    userRoles.forEach(user => {
      if (changedUsers.has(user.userId)) {
        const rolesToSubmit = user.updatedRoles || user.currentRoles;
        const currentRolesSorted = [...user.currentRoles].sort();
        const updatedRolesSorted = [...rolesToSubmit].sort();
        const hasRealChange = JSON.stringify(currentRolesSorted) !== JSON.stringify(updatedRolesSorted);

        if (hasRealChange) {
          updates.push({
            boardId: board.board_id,
            userId: user.userId,
            boardRoles: rolesToSubmit,
          });
        }
      }
    });

    if (updates.length === 0) {
      message.info('변경된 권한이 없습니다.');
      return;
    }

    onSubmit(updates);
  };

  const handleClose = () => {
    setUserRoles([]);
    setChangedUsers(new Set());
    onClose();
  };

  const columns = getBoardRoleColumns({ handleRoleChange, changedUsers });

  // ADMIN_SUB와 VISITOR 권한 제외
  const filteredUserRoles = userRoles.filter(
    user => user.role !== 'ADMIN_SUB' && user.role !== 'VISITOR'
  );

  return (
    <Modal
      title={`게시판 권한 관리 - ${board?.name || ''}`}
      open={open}
      onCancel={handleClose}
      width={800}
      centered
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isSubmitting}>
          취소
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? '저장 중...' : `저장 (${changedUsers.size}명 변경)`}
        </Button>,
    ]}
    >
      <Table
        rowKey="userId"
        columns={columns}
        dataSource={filteredUserRoles}
        loading={usersLoading || rolesLoading || isSubmitting}
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default ConferenceRoleModal;